import React from "react";
import PropTypes from "prop-types";
import {
  Table,
  TableBody,
  TableContainer,
  Paper,
  TablePagination,
} from "@mui/material";
import { getComparator, stableSort } from "./fileDataTableUtils";
import FileDataTableToolbar from "./FileDataTableToolbar";
import FileDataTableHead from "./FileDataTableHead";
import FileDataTableRow from "./FileDataTableRow";
import { useSelector } from "react-redux";
import { selectCurrentUserData } from "../../../../features/auth/authSlice";
import useAuth from "../../../../hooks/useAuth";
import { Icon } from "@iconify/react";
import { useParams } from "react-router-dom";
import useFileData from "../../../../hooks/useFileData";

const TABLE_STATE_STORAGE_KEY = "fileDataTableViewState";

const DEFAULT_FILTERS = {
  status: ["new", "edit", "revision", "released"],
  auditRelevant: null,
};

const readPersistedTableState = () => {
  try {
    const raw = sessionStorage.getItem(TABLE_STATE_STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const FileDataTable = ({ fileDatas }) => {
  let { searchId } = useParams();

  // View state (search, sorting, pagination, filters) is persisted to
  // sessionStorage so it survives the unmount/remount that happens when the
  // user opens or edits a document and then returns to the table.
  const persistedState = React.useRef(readPersistedTableState()).current;

  // An explicit searchId in the URL (deep link) takes precedence over the
  // persisted search term; the generic "show" entry point does not.
  const hasSearchIdParam = Boolean(searchId) && searchId !== "show";

  const [tableState, setTableState] = React.useState({
    filters: persistedState.filters ?? DEFAULT_FILTERS,
    filterActive: false,
    openFilter: false,
  });
  const [order, setOrder] = React.useState(persistedState.order ?? "asc");
  const [orderBy, setOrderBy] = React.useState(persistedState.orderBy ?? "");
  const [selected, setSelected] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState(
    hasSearchIdParam ? searchId : persistedState.searchTerm ?? ""
  ); // New state for search term
  const [page, setPage] = React.useState(persistedState.page ?? 0);
  const [rowsPerPage, setRowsPerPage] = React.useState(
    persistedState.rowsPerPage ?? 10
  );

  React.useEffect(() => {
    try {
      sessionStorage.setItem(
        TABLE_STATE_STORAGE_KEY,
        JSON.stringify({
          order,
          orderBy,
          searchTerm,
          page,
          rowsPerPage,
          filters: tableState.filters,
        })
      );
    } catch {
      // Ignore storage write errors (e.g. private mode / quota exceeded).
    }
  }, [order, orderBy, searchTerm, page, rowsPerPage, tableState.filters]);

  const { isAdmin } = useAuth();
  const { handleFileAction } = useFileData();

  const userData = useSelector(selectCurrentUserData);

  const handleOpenFilter = () => {
    setTableState((prev) => ({
      ...prev,
      openFilter: true,
    }));
  };

  const handleCloseFilter = (newFilters) => {
    if (newFilters) {
      setTableState((prev) => ({
        ...prev,
        openFilter: false,
        filters: { ...newFilters },
      }));
      setPage(0);
    } else {
      setTableState((prev) => ({
        ...prev,
        openFilter: false,
      }));
    }
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (!isAdmin) {
      return null;
    }
    if (selected.length > 0) {
      setSelected([]);
      return;
    }

    if (event.target.checked) {
      console.log("true");
      const newSelected = filteredRows.map((n) => n._id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, id) => {
    if (!isAdmin) {
      return null;
    }
    event.stopPropagation();
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleEditSelected = () => {
    if (!isAdmin) {
      return null;
    }
    if (selected.length === 1) {
      let selectedFileData = fileDatas.filter((f) => selected.includes(f._id));
      handleFileAction(selectedFileData[0], "SINGLE-FILEDATA");
    } else {
      handleFileAction(selected, "MULTI-FILEDATA");
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value || "");
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const searchAndFilterRows = () => {
    let data = fileDatas.filter((file) => {
      const search = searchTerm.toLowerCase();
      return (
        file?.fileName?.toLowerCase().includes(search) ||
        file?.filePath?.toLowerCase().includes(search) ||
        file?._id?.toLowerCase().includes(search)
      );
    });

    if (tableState?.filters) {
      const { filters } = tableState;

      // Status-Filter
      if (filters.status && filters.status.length > 0) {
        data = data.filter((file) => filters.status.includes(file.status));
      }

      // Auditrelevant-Filter
      if (filters.auditRelevant) {
        data = data.filter((file) => file.auditRelevant);
      }

      // Users-Filter
      if (filters.responsibleUser && filters.responsibleUser.length > 0) {
        data = data.filter((file) =>
          filters.responsibleUser.includes(file.responsibleUser._id)
        ); // assuming file.responsibleUser._id holds the user ID
      }
    }

    return data;
  };

  const filteredRows = searchAndFilterRows();

  // Keep the page in range when filtering/searching shrinks the result set,
  // otherwise pagination can land on an empty out-of-range page.
  React.useEffect(() => {
    const lastPage = Math.max(
      0,
      Math.ceil(filteredRows.length / rowsPerPage) - 1
    );
    if (page > lastPage) {
      setPage(lastPage);
    }
  }, [filteredRows.length, rowsPerPage, page]);

  const visibleRows = React.useMemo(
    () =>
      stableSort(filteredRows, getComparator(order, orderBy)).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [order, orderBy, filteredRows, page, rowsPerPage]
  );

  return (
    <Paper elevation={3}>
      <FileDataTableToolbar
        numSelected={selected.length}
        searchValue={searchTerm}
        onSearch={handleSearch}
        activeFilters={tableState.filters}
        openFilter={tableState.openFilter}
        handleOpenFilter={handleOpenFilter}
        handleCloseFilter={handleCloseFilter}
        handleEditSelected={handleEditSelected}
      />
      <TableContainer sx={{ maxHeight: "62vh" }}>
        <Table stickyHeader aria-label="collapsible table">
          <FileDataTableHead
            numSelected={selected.length}
            order={order}
            orderBy={orderBy}
            onSelectAllClick={handleSelectAllClick}
            onRequestSort={handleRequestSort}
            rowCount={fileDatas.length}
            isAdmin={isAdmin}
          />
          <TableBody>
            {visibleRows.map((file, index) => {
              const isItemSelected = isSelected(file._id);
              const labelId = `enhanced-table-checkbox-${index}`;
              return (
                <FileDataTableRow
                  key={file._id}
                  file={file}
                  handleClick={handleClick}
                  isItemSelected={isItemSelected}
                  labelId={labelId}
                  userData={userData}
                  isAdmin={isAdmin}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={filteredRows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage={"Zeilen pro Seite:"}
      />
      <div style={{ padding: 5, borderTopWidth: 2, borderTopStyle: "solid" }}>
        <b>Gesamtanzahl: {filteredRows.length}</b>
        <p style={{ marginRight: 5 }}>
          <Icon
            icon="ic:twotone-error"
            color="darkred"
            width={"2vh"}
            height={"2vh"}
          />
          = Dokument wird aktuell bearbeitet und sollte nicht verändert werden!
        </p>
      </div>
    </Paper>
  );
};

FileDataTable.propTypes = {
  fileDatas: PropTypes.array.isRequired,
  handleOpenFileDialog: PropTypes.func,
  handleOpenAssignFileDialog: PropTypes.func,
};

export default FileDataTable;
